import { Component } from '@theme/component';
import { onAnimationEnd, prefersReducedMotion, debounce, preloadImage } from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * Reads a duration/easing pair from CSS custom properties and resolves them into
 * values the Web Animations API can use directly (a number of milliseconds and a
 * literal easing string), since `element.animate()` can't resolve `var(...)` itself.
 * @param {string} durationVar
 * @param {string} easingVar
 * @returns {{ duration: number, easing: string }}
 */
function readCssTiming(durationVar, easingVar) {
  const styles = getComputedStyle(document.documentElement);
  const durationText = styles.getPropertyValue(durationVar).trim();
  const seconds = parseFloat(durationText);
  const duration = durationText.endsWith('ms') ? seconds : seconds * 1000;
  const easing = styles.getPropertyValue(easingVar).trim();

  return { duration, easing: easing || 'ease' };
}

/**
 * Gets the rect of the actual visible image content within an <img>'s box, not the
 * box itself. `object-fit: contain` letterboxes the natural-ratio image inside a
 * box that can have a very different aspect ratio (e.g. the dialog's <img> fills a
 * near-viewport-sized box while the photo itself is portrait) - using the raw box
 * for a FLIP transform badly distorts the animation (very different x/y scale
 * factors), since it's animating empty letterbox space along with the real image.
 * `object-fit: cover` has no letterboxing, so the box rect is already correct.
 * @param {HTMLImageElement} img
 * @returns {DOMRect}
 */
function getVisualContentRect(img) {
  const box = img.getBoundingClientRect();

  if (getComputedStyle(img).objectFit !== 'contain') return box;

  const naturalWidth = img.naturalWidth;
  const naturalHeight = img.naturalHeight;
  if (!naturalWidth || !naturalHeight || !box.width || !box.height) return box;

  const contentAspect = naturalWidth / naturalHeight;
  const boxAspect = box.width / box.height;

  let width, height;
  if (contentAspect > boxAspect) {
    width = box.width;
    height = box.width / contentAspect;
  } else {
    height = box.height;
    width = box.height * contentAspect;
  }

  const left = box.left + (box.width - width) / 2;
  const top = box.top + (box.height - height) / 2;

  return new DOMRect(left, top, width, height);
}

/**
 * Animates `target` growing from (or shrinking to) `source`'s current on-screen
 * position/size, using a GPU-composited transform (translate + scale) rather than
 * the View Transitions API.
 *
 * The View Transitions API captures rasterized snapshots of the source and target
 * and cross-fades/scales those bitmaps - when the two elements have very different
 * aspect ratios (a grid thumbnail vs. a full natural-ratio image), that non-uniform
 * snapshot scaling reads as janky. Animating a single, always-present <img> with
 * `transform` avoids a snapshot entirely: the browser only has to composite, never
 * re-layout or re-rasterize, across the whole animation - the same technique
 * dedicated lightbox libraries (e.g. PhotoSwipe) use for their zoom transition.
 *
 * @param {HTMLImageElement} target - The element to animate, already in its final position/size.
 * @param {HTMLImageElement} source - The element whose current rect to animate from/to.
 * @param {string} durationVar - CSS custom property name holding the duration (e.g. '--spring-d300-b0-duration').
 * @param {string} easingVar - CSS custom property name holding the easing (e.g. '--spring-d300-b0-easing').
 * @param {boolean} [reverse] - If true, animates from the natural position to source's rect instead.
 * @returns {Promise<void>}
 */
function flipAnimate(target, source, durationVar, easingVar, reverse = false) {
  if (prefersReducedMotion()) return Promise.resolve();

  const targetRect = getVisualContentRect(target);
  const sourceRect = getVisualContentRect(source);

  if (!targetRect.width || !targetRect.height || !sourceRect.width || !sourceRect.height) {
    return Promise.resolve();
  }

  const dx = sourceRect.left + sourceRect.width / 2 - (targetRect.left + targetRect.width / 2);
  const dy = sourceRect.top + sourceRect.height / 2 - (targetRect.top + targetRect.height / 2);
  const sx = sourceRect.width / targetRect.width;
  const sy = sourceRect.height / targetRect.height;

  const collapsed = `translate(${dx}px, ${dy}px) scale(${sx}, ${sy})`;
  const natural = 'translate(0, 0) scale(1, 1)';
  const { duration, easing } = readCssTiming(durationVar, easingVar);

  /* CUSTOM CODE for RUGETTE -- START */
  // .product-media has `overflow: hidden` on desktop so a zoomed-in image (via the
  // zoom-in/out buttons) doesn't spill past its box - but that same clipping cuts
  // off this open/close FLIP transform as it translates/scales the <img> toward the
  // thumbnail's position, since part of it temporarily extends past that static
  // box. Relax it for the duration of this animation only, then restore.
  const clippingAncestor = /** @type {HTMLElement | null} */ (target.closest('.product-media'));
  const previousOverflow = clippingAncestor?.style.overflow ?? '';
  if (clippingAncestor) clippingAncestor.style.overflow = 'visible';
  /* CUSTOM CODE for RUGETTE -- END */

  const animation = target.animate([{ transform: reverse ? natural : collapsed }, { transform: reverse ? collapsed : natural }], {
    duration,
    easing,
    fill: 'both',
  });

  return animation.finished
    .catch(() => {
      // Animation can be interrupted (e.g. rapid open/close) - that's fine, just settle.
    })
    .then(() => {
      animation.cancel();
      /* CUSTOM CODE for RUGETTE -- START */
      if (clippingAncestor) clippingAncestor.style.overflow = previousOverflow;
      /* CUSTOM CODE for RUGETTE -- END */
    });
}

/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('scroll', this.handleScroll);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog.removeEventListener('scroll', this.handleScroll);
  }

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    event.preventDefault();

    const { dialog, media, thumbnails } = this.refs;
    const targetImage = media[index];
    const targetThumbnail = thumbnails.children[index];

    /** @type {HTMLElement | null} */
    const sourceContainer = event.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;
    const sourceImage = sourceContainer?.querySelector('img.product-media__image') ?? sourceContainer;

    dialog.showModal();

    for (const target of [targetThumbnail, targetImage]) {
      target?.scrollIntoView({ behavior: 'instant' });
    }

    this.selectThumbnail(index, { behavior: 'instant' });

    if (sourceImage && targetImage) {
      const targetMediaImage = targetImage.querySelector('img.product-media__image');
      if (targetMediaImage) {
        await flipAnimate(targetMediaImage, sourceImage, '--spring-d300-b0-duration', '--spring-d300-b0-easing');
      }
    }
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);
    newImage.setAttribute('ref', 'image');

    // When the high-resolution image loads, replace the existing image
    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  /**
   * Handles the scroll event of the dialog, which is used to update the active thumbnail when the corresponding image is visible in the main view.
   * @param {Event} event - The scroll event.
   */
  handleScroll = debounce(async () => {
    const { media, thumbnails } = this.refs;

    const mostVisibleElement = await getMostVisibleElement(media);
    const activeIndex = media.indexOf(mostVisibleElement);
    const targetThumbnail = thumbnails.children[activeIndex];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === activeIndex}`);
    });

    this.loadHighResolutionImage(mostVisibleElement);
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }, 50);

  /**
   * Closes the zoom dialog.
   */
  async close() {
    const { dialog, media } = this.refs;

    // Find the most visible image using IntersectionObserver
    const mostVisibleElement = await getMostVisibleElement(media);

    // Get the index and set up the FLIP target/source pair
    const activeIndex = media.indexOf(mostVisibleElement);

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';

    const slide = slideshowActive ? mediaGallery.slideshow?.slides?.[activeIndex] : mediaGallery?.media?.[activeIndex];

    dialog.classList.add('dialog--closed');

    await onAnimationEnd(this.refs.thumbnails);

    if (slide) {
      const sourceImage = slide.querySelector('img.product-media__image') ?? slide;
      const targetImage = mostVisibleElement.querySelector('img.product-media__image');

      if (targetImage) {
        await flipAnimate(targetImage, sourceImage, '--spring-d220-b0-duration', '--spring-d220-b0-easing', true);
      }
    }

    this.closeDialog();
    dialog.classList.remove('dialog--closed');
  }

  closeDialog() {
    const { dialog } = this.refs;
    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /* CUSTOM CODE for RUGETTE -- START */
  /**
   * Advances to the next media item.
   */
  async next() {
    await this.#navigate(1);
  }

  /**
   * Goes back to the previous media item.
   */
  async previous() {
    await this.#navigate(-1);
  }

  /**
   * Zooms in on the currently visible media item.
   */
  async zoomIn() {
    const wrapper = await this.#getActiveDragZoomWrapper();
    wrapper?.zoomIn();
  }

  /**
   * Zooms out on the currently visible media item.
   */
  async zoomOut() {
    const wrapper = await this.#getActiveDragZoomWrapper();
    wrapper?.zoomOut();
  }

  /**
   * Finds the <drag-zoom-wrapper> for whichever media item is currently visible.
   * @returns {Promise<(HTMLElement & { zoomIn(): void, zoomOut(): void }) | null>}
   */
  async #getActiveDragZoomWrapper() {
    const { media } = this.refs;
    if (!media.length) return null;

    const mostVisibleElement = await getMostVisibleElement(media);
    return mostVisibleElement.querySelector('drag-zoom-wrapper');
  }

  /**
   * Selects the media item `direction` steps away from the currently visible one,
   * wrapping around at either end.
   * @param {number} direction - 1 for next, -1 for previous.
   */
  async #navigate(direction) {
    const { media } = this.refs;
    if (!media.length) return;

    const mostVisibleElement = await getMostVisibleElement(media);
    const currentIndex = media.indexOf(mostVisibleElement);
    const nextIndex = (currentIndex + direction + media.length) % media.length;
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';

    this.selectThumbnail(nextIndex, { behavior });
  }
  /* CUSTOM CODE for RUGETTE -- END */

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.close();
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index - The index of the thumbnail to load the high-resolution image for.
   */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media[index]) return;

    this.loadHighResolutionImage(media[index]);
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    if (!this.refs.thumbnails || !this.refs.thumbnails.children.length) return;

    // Guard if invalid
    if (isNaN(index) || index < 0 || index >= this.refs.thumbnails.children.length) return;

    const { media, thumbnails } = this.refs;
    const targetThumbnail = thumbnails.children[index];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === index}`);
    });

    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: options.behavior,
      block: 'center',
      inline: 'center',
    });

    const targetImage = media[index];

    if (targetImage) {
      targetImage.scrollIntoView({
        behavior: options.behavior,
      });

      this.loadHighResolutionImage(targetImage);
    }
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * Get the most visible element from a list of elements.
 * @param {HTMLElement[]} elements - The elements to get the most visible element from.
 * @returns {Promise<HTMLElement>} A promise that resolves to the most visible element.
 */
function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        observer.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}
